import { OptimizedImage } from "@/components/OptimizedImage";
import { useLanguage } from "@/context/LanguageContext";
import { ServiceBookingOptions } from "@/components/ServiceBookingOptions";
import { STATIC_SERVICE_SEO } from "@/config/serviceSeo";
import { useState } from "react";
import { Link } from "@/i18n/LocalizedLink";
import {
  Users, Luggage, ShieldCheck, Clock, Route, ArrowRight, Car,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { SectionHeading, Eyebrow, QuoteButton, FleetCard, LocationCard } from "@/components/shared";
import { CallButton, WhatsAppButton } from "@/components/ActionButtons";
import { useEnquiry } from "@/context/EnquiryContext";
import { SITE } from "@/config/site";
import { IMG } from "@/config/images";
import { FLEET } from "@/config/fleet";
import { PRIMARY_LOCATIONS, SECONDARY_LOCATIONS, SATNA_CARD } from "@/config/locations";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VEHICLE_CATEGORIES } from "@/config/site";
import { useTodayInIndia } from "@/lib/useTodayInIndia";
import { HOME_SERVICE_SCHEMA } from "@/config/structuredData";
import { getVehicleGuidance } from "@/config/locationVehicleGuidance";
import { TRAVELLER_SEATS } from "@/config/site";

const HeroCard = () => {
  const { t: translate, cityName } = useLanguage();
  const today = useTodayInIndia();
  const { openEnquiry } = useEnquiry();
  const [t, setT] = useState({ pickup_city: "Satna", destination: "", travel_date: "", passengers: "", vehicle_category: "" });
  const set = (k) => (e) => setT((p) => ({ ...p, [k]: e.target.value }));
  return (
    <form onSubmit={(event) => { event.preventDefault(); openEnquiry(t); }} className="rounded-2xl border border-white/10 bg-white p-5 shadow-2xl sm:p-6" data-testid="hero-enquiry-card">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500 text-slate-950"><Route className="h-4 w-4" /></span>
        <h2 className="text-lg font-extrabold text-slate-900">{translate("Plan your trip")}</h2>
      </div>
      <p className="mt-1 text-xs text-slate-500">{translate("Get a quick quote by WhatsApp or a callback.")}</p>
      <div className="mt-4 grid gap-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="h-pickup" className="text-xs font-semibold text-slate-700">{translate("Pickup")}</Label>
            <Input id="h-pickup" data-testid="hero-pickup-input" value={t.pickup_city === "Satna" ? cityName("Satna") : t.pickup_city} onChange={set("pickup_city")} placeholder={cityName("Satna")} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="h-dest" className="text-xs font-semibold text-slate-700">{translate("Destination")}</Label>
            <Input id="h-dest" data-testid="hero-destination-input" value={t.destination} onChange={set("destination")} placeholder={translate("e.g. Khajuraho")} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="h-date" className="text-xs font-semibold text-slate-700">{translate("Travel date")}</Label>
            <Input id="h-date" type="date" min={today} data-testid="hero-travel-date-input" value={t.travel_date} onChange={set("travel_date")} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="h-pax" className="text-xs font-semibold text-slate-700">{translate("Passengers")}</Label>
            <Input id="h-pax" type="number" min="1" max="999" step="1" data-testid="hero-passengers-input" value={t.passengers} onChange={set("passengers")} placeholder="4" />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="h-veh" className="text-xs font-semibold text-slate-700">{translate("Vehicle")}</Label>
          <Select value={t.vehicle_category} onValueChange={(v) => setT((p) => ({ ...p, vehicle_category: v }))}>
            <SelectTrigger id="h-veh" data-testid="hero-vehicle-category-select"><SelectValue placeholder={translate("Any vehicle")} /></SelectTrigger>
            <SelectContent>
              {VEHICLE_CATEGORIES.map((v) => <SelectItem key={v} value={v}>{translate(v)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <button
          type="submit"
          data-testid="hero-enquiry-submit-button"
          className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-base font-bold text-slate-950 shadow-lg transition-colors hover:bg-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
        >
          {translate("Continue to quote")} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
};

const STEPS = [
  { n: "1", title: "Share your trip", desc: "Tell us pickup, destination, date and passengers by form, WhatsApp or call." },
  { n: "2", title: "Get a quote", desc: "We confirm a suitable vehicle and share a fare for your journey." },
  { n: "3", title: "Travel comfortably", desc: "Your driver arrives at the agreed pickup point and time." },
];

export default function Home() {
  const { t } = useLanguage();
  return (
    <>
      <Seo
        title={t(STATIC_SERVICE_SEO.home.title)}
        description={t(STATIC_SERVICE_SEO.home.description)}
        image={IMG.heroFleet}
        jsonLd={HOME_SERVICE_SCHEMA}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#071426]">
        <div className="absolute inset-0">
          <OptimizedImage src={IMG.heroFleet} sizes="100vw" width="1264" height="848" fetchPriority="high" decoding="async" alt={t("A sedan taxi and a Tempo Traveller on a Madhya Pradesh highway, ready for local and group travel")} className="h-full w-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#071426] via-[#071426]/85 to-[#071426]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#071426] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-14 lg:pt-24 lg:pb-20">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <Eyebrow className="text-amber-400">{t("Satna · Madhya Pradesh · Central India")}</Eyebrow>
              <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight text-white">{t(STATIC_SERVICE_SEO.home.h1)}</h1>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-300 max-w-xl">{t("Taxi and Tempo Traveller services with drivers from our Satna base for local, outstation and group travel. For car hire or cab booking, share your pickup, destination and travel dates so we can confirm availability and a quote.")}</p>
              <p className="mt-3 text-sm text-slate-400" lang="hi">{SITE.hindiAssist}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <CallButton className="bg-sky-600 px-5 py-3 text-white hover:bg-sky-700" testId="hero-call-now-button" label={t("Call {phone}", { phone: SITE.phonePrimary.display })} />
                <WhatsAppButton className="bg-emerald-600 px-5 py-3 text-white hover:bg-emerald-700" testId="hero-whatsapp-button" label={t("WhatsApp Enquiry")} />
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-slate-300">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-amber-400" /> {t("Experienced drivers")}</span>
                <span className="inline-flex items-center gap-1.5"><Car className="h-4 w-4 text-amber-400" /> {t("Cars, SUVs & Tempo Travellers")}</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-amber-400" /> {t("Prebooked outstation")}</span>
              </div>
            </div>
            <HeroCard />
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <ServiceBookingOptions page="home" />
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/services/" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 hover:bg-slate-50" data-testid="home-view-services-link">{t("All taxi services")} <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/tempo-traveller/" className="inline-flex items-center gap-2 rounded-full bg-[#0B1E36] px-5 py-2.5 font-semibold text-white hover:bg-[#132D50]" data-testid="home-tempo-link">{t("Tempo Traveller for groups")} <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      {/* Tempo Traveller feature band */}
      <section className="bg-[#0B1E36]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div className="order-2 lg:order-1 overflow-hidden rounded-2xl shadow-2xl">
              <OptimizedImage src={IMG.tempo} sizes="(min-width: 1024px) 600px, calc(100vw - 32px)" width="1264" height="848" decoding="async" alt={t("White Force Tempo Traveller minibus parked on a highway, ideal for group travel")} className="w-full object-cover" loading="lazy" />
            </div>
            <div className="order-1 lg:order-2">
              <SectionHeading light eyebrow={t("Group travel specialists")} title={t("Tempo Traveller hire and rental in Satna")} intro={t("Plan Tempo Traveller rental with a driver for wedding guests, pilgrimage groups or company outings. Share your passenger count, luggage and planned stops so we can confirm the vehicle and quote for your journey.")} />
              <div className="mt-6 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3" data-testid="home-tempo-seating-band">
                <p className="text-base sm:text-lg font-bold text-amber-300">{t("{seats} seater Traveller options", { seats: TRAVELLER_SEATS.join(", ") })}</p>
                <p className="mt-1 text-sm text-slate-300">{t("Published 13–26 seat totals include one driver; 9-seater capacity and fare are quoted separately. Availability is confirmed for your dates.")}</p>
              </div>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {["Passenger + driver seating", "Luggage planning", "Experienced drivers", "Multi-day tour planning"].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-200"><Users className="h-4 w-4 text-amber-400" aria-hidden="true" /> {t(f)}</li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap gap-3">
                <QuoteButton prefill={{ vehicle_category: "Tempo Traveller" }} testId="home-tempo-quote-button">{t("Request Tempo Traveller quote")}</QuoteButton>
                <Link to="/tempo-traveller/" className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-5 py-3 font-semibold text-slate-200 hover:bg-white/5" data-testid="home-tempo-details-link">{t("See details")}</Link>
              </div>
              <p className="mt-3 text-xs text-slate-400"><Luggage className="inline h-3.5 w-3.5 mr-1" />{t("Confirm luggage space and the assigned vehicle specifications before booking.")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Fleet preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <SectionHeading eyebrow={t("Our fleet")} title={t("Car hire and vehicle options in Satna")} intro={t(getVehicleGuidance("/"))} />
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-600">{t("For car rental with driver in Satna, request a listed model and share your route. Exact availability, seating, luggage space and vehicle specifications are confirmed with your quotation.")}</p>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FLEET.slice(0, 3).map((item) => <FleetCard key={item.id} item={item} />)}
        </div>
        <div className="mt-8">
          <Link to="/fleet/" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 hover:bg-slate-50" data-testid="home-view-fleet-link">{t("View full fleet")} <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      {/* Service areas */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <SectionHeading eyebrow={t("Where we travel")} title={t("Service areas across Central India")} intro={t("Primary service areas around Satna, plus prebooked longer-distance destinations. Tap a location for local pickup details, services and FAQs.")} />
          <div className="mt-8">
            <h3 className="text-sm font-bold uppercase tracking-wider text-amber-600">{t("Primary service areas")}</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[SATNA_CARD, ...PRIMARY_LOCATIONS].map((l) => <LocationCard key={l.slug} item={l} />)}
            </div>
            <h3 className="mt-8 text-sm font-bold uppercase tracking-wider text-slate-500">{t("Secondary (prebooked) destinations")}</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {SECONDARY_LOCATIONS.map((l) => <LocationCard key={l.slug} item={l} />)}
            </div>
          </div>
        </div>
      </section>

      {/* Booking steps */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <SectionHeading center eyebrow={t("Simple booking")} title={t("How booking works")} intro={t("No accounts, no online payments — just a quick enquiry and an honest quote.")} />
        <div className="mt-10 grid gap-6 sm:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n} className="relative rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#0B1E36] text-xl font-extrabold text-amber-400">{s.n}</span>
              <h3 className="mt-4 text-lg font-bold text-slate-900">{t(s.title)}</h3>
              <p className="mt-2 text-sm text-slate-600">{t(s.desc)}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex justify-center"><QuoteButton testId="home-steps-quote-button">{t("Start your enquiry")}</QuoteButton></div>
      </section>

      {/* CTA band */}
      <section className="bg-amber-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl font-extrabold text-slate-950">{t("Ready to book your journey?")}</h2>
            <p className="mt-1 text-slate-900/80">{t("Call or WhatsApp {phone} for a quick quote.", { phone: SITE.phonePrimary.display })}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <CallButton className="bg-[#0B1E36] px-5 py-3 text-white hover:bg-[#132D50]" testId="cta-call-button" label={t("Call Now")} />
            <WhatsAppButton className="bg-white px-5 py-3 text-emerald-700 hover:bg-slate-50" testId="cta-whatsapp-button" />
          </div>
        </div>
      </section>
    </>
  );
}
